import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Send,
  Star,
  Facebook,
  Phone,
  Mail,
  Youtube,
  MapPin,
  ExternalLink,
  Image as ImageIcon,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { LanguageSwitcher } from './LanguageSwitcher';
import { GrabLogo, ShopeeLogo, XanhSMLogo } from './PlatformLogos';

interface FeedbackForm {
  rating: number | null;
  comment: string;
  category: string;
  platform: string;
  orderId?: string;
  contactInfo?: string;
  images?: string[]; // Base64 encoded images
}

export function Feedback() {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FeedbackForm>({
    rating: null,
    comment: '',
    category: 'food-beverage',
     platform: 'dine-in',
    orderId: '',
    contactInfo: '',
    images: [],
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const MAX_IMAGES = 5;
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleRatingClick = (rating: number) => {
    setFormData((prev) => ({ ...prev, rating }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach((file) => {
      if (formData.images && formData.images.length >= MAX_IMAGES) {
        alert(translate('feedback.images.maxReached'));
        return;
      }

      if (file.size > MAX_IMAGE_SIZE) {
        alert(translate('feedback.images.tooLarge'));
        return;
      }

      if (!file.type.startsWith('image/')) {
        alert(translate('feedback.images.invalidType'));
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        setFormData((prev) => ({
          ...prev,
          images: [...(prev.images || []), base64],
        }));
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const apiUrl = import.meta.env.VITE_API_ROOT || '/api';
      const response = await fetch(`${apiUrl}/customer_feedbacks/public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rating: formData.rating,
          comment: formData.comment,
          category: formData.category,
           platform: formData.platform,
          orderId: formData.orderId || undefined,
          contactInfo: formData.contactInfo || undefined,
          images: formData.images || undefined,
        }),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: 'Failed to submit feedback' }));
        throw new Error(error.message || 'Failed to submit feedback');
      }

      // Generate unique token for thank you page
      const token =
        Date.now().toString(36) + Math.random().toString(36).substr(2);

      // Navigate to thank you page with rating in URL
      navigate(`/thank-you?token=${token}&rating=${formData.rating}`, {
        replace: true,
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert(
        error instanceof Error
          ? error.message
          : 'Failed to submit feedback. Please try again.'
      );
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-start justify-center p-4 pt-8">
      <div className="w-full max-w-2xl">
        <div className="bg-card border border-border rounded-lg shadow-lg p-6 md:p-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <MessageSquare className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">
                  {translate('feedback.title')}
                </h1>
                <p className="text-xs text-muted-foreground">
                  {translate('feedback.subtitle')}
                </p>
              </div>
            </div>
            <LanguageSwitcher />
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Rating */}
            <div>
              <label className="block text-sm font-medium mb-3">
                {translate('feedback.rating.label')}
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => handleRatingClick(rating)}
                    className={cn(
                      'flex-1 p-2 rounded-lg border-2 transition-all',
                      'hover:scale-105 active:scale-95',
                      formData.rating && rating <= formData.rating
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    )}
                  >
                    <Star
                      className={cn(
                        'w-5 h-5 mx-auto',
                        formData.rating && rating <= formData.rating
                          ? 'fill-primary text-primary'
                          : 'text-muted-foreground'
                      )}
                    />
                    <span className="text-xs mt-1 block text-center">
                      {rating}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Platform */}
            <div>
              <label className="block text-sm font-medium mb-3">
                {translate('feedback.platform.label')}
              </label>
              <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                {[
                  {
                    value: 'dine-in',
                    label: translate('feedback.platform.dineIn'),
                    icon: null,
                    shortLabel: translate('feedback.platform.dineIn'),
                  },
                  {
                    value: 'delivery',
                    label: translate('feedback.platform.delivery'),
                    icon: null,
                    shortLabel: translate('feedback.platform.delivery'),
                  },
                  {
                    value: 'grab',
                    label: 'Grab',
                    icon: <GrabLogo />,
                    shortLabel: 'Grab',
                  },
                  {
                    value: 'shopee-food',
                    label: 'Shopee',
                    icon: <ShopeeLogo />,
                    shortLabel: 'Shopee',
                  },
                  {
                    value: 'xanh-sm',
                    label: 'Xanh SM',
                    icon: <XanhSMLogo />,
                    shortLabel: 'Xanh SM',
                  },
                ].map((platform) => (
                  <button
                    key={platform.value}
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        platform: platform.value,
                        // Clear order ID if switching to non-delivery platform
                        orderId:
                          platform.value === 'grab' ||
                          platform.value === 'shopee-food' ||
                          platform.value === 'xanh-sm'
                            ? prev.orderId
                            : '',
                      }));
                    }}
                    className={cn(
                      'p-1.5 sm:p-2 rounded-lg border-2 transition-all text-xs font-medium flex flex-col items-center justify-center gap-0.5 sm:gap-1',
                      'hover:scale-105 active:scale-95 min-h-[50px] sm:min-h-[60px]',
                      formData.platform === platform.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50 text-foreground'
                    )}
                    title={platform.label}
                  >
                    {platform.icon ? (
                      <>
                        <div
                          className={cn(
                            'flex-shrink-0',
                            formData.platform === platform.value
                              ? 'opacity-100'
                              : 'opacity-80'
                          )}
                        >
                          {platform.icon}
                        </div>
                        <span className="text-center leading-tight text-[9px] sm:text-[10px] hidden sm:block">
                          {platform.label}
                        </span>
                      </>
                    ) : (
                      <span className="text-center leading-tight text-[9px] sm:text-xs px-0.5">
                        {platform.shortLabel}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Order ID - Only show for delivery platforms */}
            {(formData.platform === 'grab' ||
              formData.platform === 'shopee-food' ||
              formData.platform === 'xanh-sm') && (
              <div>
                <label
                  htmlFor="orderId"
                  className="block text-sm font-medium mb-2"
                >
                  {translate('feedback.orderId.label')}
                  {formData.platform === 'grab' && (
                    <span className="text-xs text-muted-foreground ml-2">
                      ({translate('feedback.orderId.grabFormat')})
                    </span>
                  )}
                </label>
                <input
                  id="orderId"
                  type="text"
                  value={formData.orderId || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      orderId: e.target.value,
                    }))
                  }
                  placeholder={translate(
                    `feedback.orderId.placeholder.${formData.platform}`
                  )}
                  className="w-full p-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {translate(`feedback.orderId.hint.${formData.platform}`)}
                </p>
              </div>
            )}

            {/* Category */}
            <div>
              <label
                htmlFor="category"
                className="block text-sm font-medium mb-2"
              >
                {translate('feedback.category.label')}
              </label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    category: e.target.value,
                  }))
                }
                className="w-full p-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="food-beverage">
                  {translate('feedback.category.foodBeverage')}
                </option>
                <option value="service">
                  {translate('feedback.category.service')}
                </option>
                <option value="atmosphere">
                  {translate('feedback.category.atmosphere')}
                </option>
                <option value="menu">
                  {translate('feedback.category.menu')}
                </option>
                <option value="staff">
                  {translate('feedback.category.staff')}
                </option>
                <option value="general">
                  {translate('feedback.category.general')}
                </option>
                <option value="other">
                  {translate('feedback.category.other')}
                </option>
               </select>
            </div>

            {/* Contact Info */}
            <div>
              <label
                htmlFor="contactInfo"
                className="block text-sm font-medium mb-2"
              >
                {translate('feedback.contactInfo.label')}
              </label>
              <input
                id="contactInfo"
                type="text"
                value={formData.contactInfo || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    contactInfo: e.target.value,
                  }))
                }
                placeholder={translate('feedback.contactInfo.placeholder')}
                className="w-full p-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Comment */}
            <div>
              <label
                htmlFor="comment"
                className="block text-sm font-medium mb-2"
              >
                {translate('feedback.comment.label')}
              </label>
              <textarea
                id="comment"
                value={formData.comment}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    comment: e.target.value,
                  }))
                }
                rows={6}
                placeholder={translate('feedback.comment.placeholder')}
                className="w-full p-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {translate('feedback.images.label')} (
                {formData.images?.length || 0}/{MAX_IMAGES})
              </label>
              <div className="space-y-3">
                {/* Image Preview Grid */}
                {formData.images && formData.images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border border-border"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          aria-label={`Remove image ${index + 1}`}
                          className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                {(!formData.images || formData.images.length < MAX_IMAGES) && (
                  <label className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors cursor-pointer">
                    <ImageIcon className="w-5 h-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {translate('feedback.images.upload')}
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                )}
                <p className="text-xs text-muted-foreground">
                  {translate('feedback.images.hint')}
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!formData.rating || !formData.comment.trim()}
              className={cn(
                'w-full p-4 rounded-lg font-medium transition-all',
                'bg-primary text-primary-foreground',
                'hover:bg-primary/90 active:scale-95',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'flex items-center justify-center gap-2'
              )}
            >
              <Send className="w-4 h-4" />
              {translate('feedback.submit')}
            </button>
          </form>

          {/* Contact Channels */}
          <div className="mt-4 pt-3 border-t border-border">
            <h3 className="text-xs font-medium mb-1.5 text-foreground">
              {translate('feedback.contact.title')}
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {import.meta.env.VITE_FACEBOOK_URL && (
                <a
                  href={import.meta.env.VITE_FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'flex items-center gap-1 px-2.5 py-1 rounded-md',
                    'bg-background border border-border text-foreground',
                    'hover:bg-accent transition-colors',
                    'text-xs font-medium'
                  )}
                >
                  <Facebook className="w-3 h-3" />
                  {translate('feedback.contact.facebook')}
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
              {import.meta.env.VITE_HOTLINE && (
                <a
                  href={`tel:${import.meta.env.VITE_HOTLINE}`}
                  className={cn(
                    'flex items-center gap-1 px-2.5 py-1 rounded-md',
                    'bg-background border border-border text-foreground',
                    'hover:bg-accent transition-colors',
                    'text-xs font-medium'
                  )}
                >
                  <Phone className="w-3 h-3" />
                  {translate('feedback.contact.hotline')} (
                  {import.meta.env.VITE_HOTLINE})
                </a>
              )}
              {import.meta.env.VITE_ZALO_URL && (
                <a
                  href={import.meta.env.VITE_ZALO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'flex items-center gap-1 px-2.5 py-1 rounded-md',
                    'bg-background border border-border text-foreground',
                    'hover:bg-accent transition-colors',
                    'text-xs font-medium'
                  )}
                >
                  <Phone className="w-3 h-3" />
                  {translate('feedback.contact.zalo')}
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
              {import.meta.env.VITE_EMAIL && (
                <a
                  href={`mailto:${import.meta.env.VITE_EMAIL}`}
                  className={cn(
                    'flex items-center gap-1 px-2.5 py-1 rounded-md',
                    'bg-background border border-border text-foreground',
                    'hover:bg-accent transition-colors',
                    'text-xs font-medium'
                  )}
                >
                  <Mail className="w-3 h-3" />
                  {translate('feedback.contact.email')}
                </a>
              )}
              {import.meta.env.VITE_YOUTUBE_URL && (
                <a
                  href={import.meta.env.VITE_YOUTUBE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'flex items-center gap-1 px-2.5 py-1 rounded-md',
                    'bg-background border border-border text-foreground',
                    'hover:bg-accent transition-colors',
                    'text-xs font-medium'
                  )}
                >
                  <Youtube className="w-3 h-3" />
                  {translate('feedback.contact.youtube')}
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
              {import.meta.env.VITE_GOOGLE_MAPS_URL && (
                <a
                  href={import.meta.env.VITE_GOOGLE_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'flex items-center gap-1 px-2.5 py-1 rounded-md',
                    'bg-background border border-border text-foreground',
                    'hover:bg-accent transition-colors',
                    'text-xs font-medium'
                  )}
                >
                  <MapPin className="w-3 h-3" />
                  {translate('feedback.contact.googleMaps')}
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
